import { type Company, SHIFT_STATUS_LABELS, type ShiftStatus } from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, Fuel } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { CompanyPicker, companySearchShape, useAccountingCompany } from "../accounting/company.js";
import { useCompanyStations } from "../accounting/shared.js";
import { useStationScope } from "../cashflow/shared.js";
import { SHIFT_STATUS_TONE } from "./shift-values.js";

/** Vardiya ekranlarının ortak arama parametreleri: şirket (defter) ve istasyon (operasyon). */
export const shiftsSearchShape = {
  ...companySearchShape,
  station: z.string().uuid().optional().catch(undefined),
};

export interface ShiftsContext {
  companyId: string;
  company: Company;
}

/**
 * Vardiya ekranlarının ortak çerçevesi: başlık + ŞİRKET seçici (muhasebe / akaryakıt ekranlarıyla aynı
 * `?company=` parametresi). İstasyon seçimi sayfanın içindedir (`useShiftStation`), çünkü kimi ekranlarda
 * filtre çubuğunun içinde durur.
 */
export function ShiftsPage({
  title,
  description,
  breadcrumb,
  actions,
  children,
}: {
  title: string;
  description?: string;
  /** "Vardiya" kökünden SONRAKİ öğeler; verilmezse yalnızca sayfa başlığı eklenir. */
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode | ((context: ShiftsContext) => ReactNode);
  children: (context: ShiftsContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  useDocumentTitle(title);
  const context = accounting.company
    ? { companyId: accounting.company.id, company: accounting.company }
    : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.shifts") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          <>
            <CompanyPicker accounting={accounting} />
            {context ? (typeof actions === "function" ? actions(context) : actions) : null}
          </>
        }
      />
      {accounting.error ? (
        <Alert tone="danger">{errorMessage(accounting.error)}</Alert>
      ) : accounting.isLoading ? (
        <SkeletonRows rows={6} />
      ) : context === null ? (
        <EmptyState
          headingLevel={2}
          icon={<Building2 />}
          title={t("accounting.company.noneTitle")}
          description={t("accounting.company.noneDescription")}
        />
      ) : (
        // Şirket değişince sayfanın yerel durumu (seçili vardiya, sekme, filtre) sıfırlanır.
        <div key={context.companyId} className="grid gap-4">
          {children(context)}
        </div>
      )}
    </>
  );
}

export interface ShiftStationOption {
  id: string;
  code: string;
  name: string;
}

export interface ShiftStationSelection {
  stations: ShiftStationOption[];
  stationId: string | null;
  /** Tek istasyonlu kiracıda ve görünür tek istasyonda seçici çizilmez (arayüz sadeleşir). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string) => void;
}

/**
 * Vardiya İSTASYON bazındadır. Seçim URL'de taşınır (`?station=`), kapsamı kısıtlı kullanıcıya yalnızca
 * kendi istasyonları gösterilir ve tek istasyonlu kiracıda seçici gizlenir (AGENTS.md §2).
 */
export function useShiftStation(companyId: string): ShiftStationSelection {
  const stations = useCompanyStations(companyId);
  const scope = useStationScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible = (stations.data ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const selected = visible.find((station) => station.id === search.station) ?? visible[0] ?? null;
  return {
    stations: visible,
    stationId: selected?.id ?? null,
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: stations.isPending,
    error: stations.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({ ...previous, station: stationId })) as never,
        replace: true,
      }),
  };
}

export function ShiftStationField({
  selection,
  className = "w-64",
}: {
  selection: ShiftStationSelection;
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("shifts.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.stationId ?? ""}
          onChange={(event) => selection.select(event.target.value)}
        >
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

/** Hiç istasyon görünmüyorsa (kapsam ya da kurulum eksik) ekranın geri kalanı çizilmez. */
export function NoStations(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Fuel />}
      title={t("shifts.noStationsTitle")}
      description={t("shifts.noStationsDescription")}
      action={
        <Link to="/ayarlar/istasyonlar" className="text-sm underline underline-offset-4">
          {t("menu.stations")}
        </Link>
      }
    />
  );
}

export function ShiftStatusBadge({ status }: { status: ShiftStatus }): ReactElement {
  return <StatusBadge tone={SHIFT_STATUS_TONE[status]}>{SHIFT_STATUS_LABELS[status]}</StatusBadge>;
}

/** Tanım listesi satırı (`<dl>` içinde kullanılır). */
export function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}
